import {range} from '@benchling/js-utils';
import {useCallback} from 'react';

import * as analytics from 'analytics/Analytics';
import {structuredTableActionOrigin} from 'analytics/Util';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {ClickableContextMenuOption, ContextMenuDisplayGroup} from 'components/ContextMenu';
import {ActionMethod, ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {PerformSingleInputTableOpFunc} from 'notebook/mediocre/EditorTypes';
import {guessColumnDataType} from 'notebook/structured-tables/columns/ColumnDataType';
import {getStructuredTableAdapterFromTable} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableHelper from 'notebook/structured-tables/StructuredTableHelper';
import {SINGLE_INPUT_CONTEXT_MENU_OPTION_ID} from 'notebook/tables/core-ui/TableAxisCell';
import * as TableConstants from 'notebook/tables/TableConstants';
import {areTypedValuesEquivalent} from 'notebook/tables/typed-values/DataTypes';
import {FileAnalyticsParams} from 'notebook/util/analytics/FileAnalyticsUtils';
import {ButtonStyle} from 'ui-library/buttons/Button';
import APIUtil from 'util/APIUtil';
import {confirm} from 'util/Modals';

export interface GetSingleInputContextMenuOptionsParams {
  tableIndex: number;
  fileId: string | null;
  structuredTable: UnknownStructuredTable;
  mergeIntoSingleInput: PerformSingleInputTableOpFunc;
  unmergeSingleInput: PerformSingleInputTableOpFunc;
  storeState: () => void;
}

export default function useGetSingleInputContextMenuOptions({
  tableIndex,
  fileId,
  structuredTable,
  mergeIntoSingleInput,
  unmergeSingleInput,
  storeState,
}: GetSingleInputContextMenuOptionsParams): (
  selectionRange: TableConstants.SelectionRange | null,
  fileAnalyticsParams: FileAnalyticsParams | null
) => Array<ClickableContextMenuOption> {
  return useCallback(
    (
      selectionRange: TableConstants.SelectionRange | null,
      fileAnalyticsParams: FileAnalyticsParams | null
    ): Array<ClickableContextMenuOption> => {
      if (!BenchlingConfig.ELN_ENABLE_STRUCTURED_TABLE_COLUMN_SINGLE_INPUTS) {
        return [];
      }
      const structuredTableAdapter = getStructuredTableAdapterFromTable(structuredTable);
      if (
        // Disallow outside the notebook
        !fileId ||
        // Disallow for legacy workflows
        APIUtil.isStageEntry(fileId) ||
        APIUtil.isStageEntryTemplate(fileId) ||
        !selectionRange ||
        !structuredTableAdapter.allowSingleInputs(structuredTable)
      ) {
        return [];
      }
      const selectedColumns = structuredTable.columns.filter(
        (_column, index) => index >= selectionRange.minIndex && index <= selectionRange.maxIndex
      );
      // ensure at least one column in real range is selected
      if (!selectedColumns.length) {
        return [];
      }
      const isSingleInput = Boolean(selectedColumns[0].isSingleInput);
      // ensure all selected columns are in the same single input state
      if (!selectedColumns.every((column) => Boolean(column.isSingleInput) === isSingleInput)) {
        return [];
      }
      // disable the option if any selected columns do not allow merged cells
      const disabled = selectedColumns.some(
        (column) => !structuredTableAdapter.allowMergedCellRange(structuredTable, column)
      );
      return [
        {
          id: SINGLE_INPUT_CONTEXT_MENU_OPTION_ID,
          name: isSingleInput
            ? `Convert single ${pluralize(selectedColumns.length, 'input')} into ${pluralize(selectedColumns.length, 'column')}`
            : `Convert ${pluralize(selectedColumns.length, 'column')} into single ${pluralize(selectedColumns.length, 'input')}`,
          disabled,
          tooltip:
            disabled && !isSingleInput
              ? `Cannot merge selected ${pluralize(selectedColumns.length, 'column')} into single input`
              : undefined,
          click: () => {
            const toSingleInput = !isSingleInput;
            if (toSingleInput) {
              checkValuesBeforeMergeSingleInput(structuredTable, selectionRange, () => {
                storeState(); // for undo/redo history
                mergeIntoSingleInput(tableIndex, selectionRange.minIndex, selectionRange.maxIndex);
                trackSingleInputConversion(structuredTable, fileAnalyticsParams, {
                  startIndex: selectionRange.minIndex,
                  endIndex: selectionRange.maxIndex,
                  toSingleInput: true,
                });
              });
            } else {
              storeState(); // for undo/redo history
              unmergeSingleInput(tableIndex, selectionRange.minIndex, selectionRange.maxIndex);
              trackSingleInputConversion(structuredTable, fileAnalyticsParams, {
                startIndex: selectionRange.minIndex,
                endIndex: selectionRange.maxIndex,
                toSingleInput: false,
              });
            }
          },
          displayGroup: ContextMenuDisplayGroup.MIDDLE,
        },
      ];
    },
    [fileId, mergeIntoSingleInput, storeState, structuredTable, tableIndex, unmergeSingleInput]
  );
}

/** When merging a column into a Single Input, warn if any of the values in the column are different. */
function checkValuesBeforeMergeSingleInput(
  structuredTable: UnknownStructuredTable,
  selectionRange: TableConstants.SelectionRange,
  mergeIntoSingleInputCallback: () => void
): void {
  // go through each column and see if there are any differing values
  const foundNonMatchingValues = range(selectionRange.minIndex, selectionRange.maxIndex).some(
    (columnIndex) => {
      const cellsInColumn = StructuredTableHelper.getCellsInColumn(structuredTable, columnIndex);
      return !areAllCellsEquivalent(cellsInColumn);
    }
  );
  if (foundNonMatchingValues) {
    confirm(
      'Convert columns to single inputs',
      'Converting columns to single inputs will only preserve the values from the top row. Convert anyway?',
      ButtonStyle.NEGATIVE,
      {
        enableRedesign: true,
        onSubmit: mergeIntoSingleInputCallback,
      }
    );
    // if no differing values are found, just merge
  } else {
    mergeIntoSingleInputCallback();
  }
}

/** Returns true if all cells in the array have equivalent values (ignores formatting). */
function areAllCellsEquivalent(cells: Array<TableConstants.Cell>): boolean {
  if (!cells.length) {
    return true;
  }
  const [firstCell, ...restCells] = cells;
  return restCells.every(
    (cell) =>
      cell.text === firstCell.text &&
      areTypedValuesEquivalent(cell.typedValue ?? null, firstCell.typedValue ?? null)
  );
}

interface SingleInputAnalyticsParams {
  // First index of sequence of columns or single inputs to convert, inclusive.
  startIndex: number;
  // Last index of sequence of columns or single inputs to convert, inclusive.
  endIndex: number;
  // Whether it's converting a column to a single input or vice versa.
  toSingleInput: boolean;
}

function trackSingleInputConversion(
  structuredTable: UnknownStructuredTable,
  fileAnalyticsParams: FileAnalyticsParams | null,
  singleInputParmas: SingleInputAnalyticsParams
): void {
  const {startIndex, endIndex, toSingleInput} = singleInputParmas;
  const analyticsParams = {
    actionOrigin: structuredTableActionOrigin(structuredTable),
    actionMethod: ActionMethod.STRUCTURED_TABLE_COLUMN_MENU,
    ...fileAnalyticsParams,
    // Structured table information
    is_config: structuredTable.isConfig,
    structured_table_api_identifier: structuredTable.apiId ?? null,
    structured_table_provenance_uuid: structuredTable.provenanceUuid,
    structured_table_kind: structuredTable.kind,
    structured_table_mode: (structuredTable.metadata as {mode?: string}).mode ?? null,
    structured_table_metadata: JSON.stringify(structuredTable.metadata),
    structured_table_num_rows: structuredTable.rows.length,
    structured_table_num_columns: structuredTable.columns.length,
  };

  const columnDefinitions =
    getStructuredTableAdapterFromTable(structuredTable).getColumnDefinitions(structuredTable);
  for (let i = startIndex; i <= endIndex; i++) {
    const columnType =
      columnDefinitions[i] && guessColumnDataType(columnDefinitions[i], structuredTable)?.type;
    analytics.trackEvent(
      toSingleInput
        ? ELN_ANALYTICS_ACTIONS.CONVERTED_COLUMN_TO_SINGLE_INPUT
        : ELN_ANALYTICS_ACTIONS.CONVERTED_COLUMN_FROM_SINGLE_INPUT,
      {
        ...analyticsParams,
        // Single input information
        index: i,
        columnType,
      }
    );
  }
}
